<script>
import Notification from '@/services/Notification'
import validationMessages from '@/components/validations'
import { required, maxLength } from "vuelidate/lib/validators";
import Swal from "sweetalert2";

export default {
  components: {
    validationMessages,
  },
  data() {
    return {
      csrf_token: localStorage.getItem('csrf_token'),
      showModal: false,
      tryingToSubmit: false,
      submitted: false,
      type_code: "",
      parent: "",
      type_name: "",
      fcm_type: "",
      localization_key_title: "",
      localization_key_desc: "",
      active: false,
      system: false,

      parentOptions: [],
      parentOptionsLoading: false,
    };
  },
  validations: {
    type_code: { required, maxLength: maxLength(64) },
    parent: { maxLength: maxLength(64) },
    type_name: { required, maxLength: maxLength(64) },
    fcm_type: { required,maxLength: maxLength(16) },
    localization_key_title: { required, maxLength: maxLength(64) },
    localization_key_desc: { required, maxLength: maxLength(64) },
  },

  methods: {
    formatTypeCode(value) {
      this.type_code = value
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '');
    },

    async getParentOptions() {
      this.parentOptionsLoading = true;
      try {
        const response = await Notification.getNotificationTypesSetup();
        this.parentOptions = response.data.data
            .filter((item) => !item.parent)
            .map((item) => ({
              value: item.type_code,
              text: `${item.type_name} (${item.type_code})`,
            }));
      } catch (error) {
        this.parentOptions = [];
      } finally {
        this.parentOptionsLoading = false;
      }
    },

    addNotificationType() {
      this.tryingToSubmit = true;
      this.submitted = true;
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.tryingToSubmit = false;
        return;
      }

      const data = {
        type_code: this.type_code,
        type_name: this.type_name,
        localization_key_title: this.localization_key_title,
        localization_key_desc: this.localization_key_desc,
        active: this.active ? 1 : 0,
        system: this.system ? 'Y' : 'N',
      };
      if (this.parent) data.parent = this.parent;
      if (this.fcm_type) data.fcm_type = this.fcm_type;

      Notification.createNotificationTypeSetup(data)
          .then(() => {
            Swal.fire("Success", "Notification Type Added!", "success");
            this.closeModal();
            this.refreshData();
          })
          .catch((error) => {
            const errorMsg = error.response?.data?.error || "Failed to add notification type";
            Swal.fire("Error", errorMsg, "error");
          })
          .finally(() => {
            this.tryingToSubmit = false;
          });
    },
    closeModal() {
      this.showModal = false;
    },
    resetProps() {
      this.submitted = false;
      this.type_code = '';
      this.parent = '';
      this.type_name = '';
      this.fcm_type = '';
      this.localization_key_title = '';
      this.localization_key_desc = '';
      this.active = false;
      this.system = false;
      this.$v.$reset();
    },
    refreshData() {
      this.$emit("refreshScreen");
    },
  }
};
</script>

<template>
  <b-modal id="add_notification_type" size="lg" v-model="showModal" @shown="getParentOptions" @hidden="resetProps" title-class="font-18">
    <template v-slot:modal-header>
      <div class="w-100">
        <h5 class="modal-title mb-0">Add Notification Type</h5>
        <p class="text-muted mb-0 mt-2">Create a new notification type</p>
      </div>
      <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
    </template>
    <form @submit.prevent="addNotificationType">
      <div class="card mb-3">
        <div class="card-body py-2">
          <h4 class="card-title mb-1">Basic Information</h4>
          <p class="text-muted mb-3">Essential details about the notification type</p>
          <div class="row">
            <div class="col-md-6">
              <div class="mb-2">
                <label class="mb-1">Type Code *</label>
                <b-form-input :value="type_code" @input="formatTypeCode" :state="submitted && $v.type_code.$error ? false : null"></b-form-input>
              </div>
              <validationMessages v-if="submitted" :fieldName="'Type Code'" :validationName="$v.type_code">
              </validationMessages>
            </div>
            <div class="col-md-6">
              <div class="mb-2">
                <label class="mb-1">Type Name *</label>
                <b-form-input v-model="type_name" :state="submitted && $v.type_name.$error ? false : null"></b-form-input>
              </div>
              <validationMessages v-if="submitted" :fieldName="'Type Name'" :validationName="$v.type_name">
              </validationMessages>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <div class="mb-2">
                <label class="mb-1">Parent</label>
                <b-form-select v-model="parent" :disabled="parentOptionsLoading" :state="submitted && $v.parent.$error ? false : null">
                  <template #first>
                    <b-form-select-option value="">None</b-form-select-option>
                  </template>
                  <b-form-select-option v-for="option in parentOptions" :key="option.value" :value="option.value">
                    {{ option.text }}
                  </b-form-select-option>
                </b-form-select>
                <small class="text-muted">Leave this empty to make the new type a parent type. Choosing a parent will make the new type a child of it.</small>
              </div>
              <validationMessages v-if="submitted" :fieldName="'Parent'" :validationName="$v.parent">
              </validationMessages>
            </div>
            <div class="col-md-6">
              <div class="mb-2">
                <label class="mb-1">FCM Type</label>
                <b-form-select v-model="fcm_type" :state="submitted && $v.fcm_type.$error ? false : null">
                  <template #first>
                    <b-form-select-option value="">None</b-form-select-option>
                  </template>
                  <b-form-select-option value="topic">topic</b-form-select-option>
                  <b-form-select-option value="batch">batch</b-form-select-option>
                </b-form-select>
              </div>
              <validationMessages v-if="submitted" :fieldName="'FCM Type'" :validationName="$v.fcm_type">
              </validationMessages>
            </div>
          </div>
        </div>
      </div>

      <div class="card mb-3">
        <div class="card-body py-2">
          <h4 class="card-title mb-1">
            <i class="uil uil-comment-alt-message me-2"></i>Localization
          </h4>
          <p class="text-muted mb-3">Localization keys used to translate this notification type</p>
          <div class="row">
            <div class="col-md-6">
              <div class="mb-2">
                <label class="mb-1">Localization Key Title *</label>
                <b-form-input v-model="localization_key_title" :state="submitted && $v.localization_key_title.$error ? false : null"></b-form-input>
              </div>
              <validationMessages v-if="submitted" :fieldName="'Localization Title'" :validationName="$v.localization_key_title">
              </validationMessages>
            </div>
            <div class="col-md-6">
              <div class="mb-2">
                <label class="mb-1">Localization Key Description *</label>
                <b-form-input v-model="localization_key_desc" :state="submitted && $v.localization_key_desc.$error ? false : null"></b-form-input>
              </div>
              <validationMessages v-if="submitted" :fieldName="'Localization Description'" :validationName="$v.localization_key_desc">
              </validationMessages>
            </div>
          </div>
        </div>
      </div>

      <div class="card mb-3">
        <div class="card-body py-2">
<!--          <div class="d-flex align-items-center justify-content-between">-->
<!--            <div>-->
<!--              <h4 class="card-title mb-0">Active</h4>-->
<!--              <p class="text-muted mb-0 mt-1" style="font-size:13px;">Enable this notification type</p>-->
<!--            </div>-->
<!--            <div class="form-check form-switch form-switch-lg mb-0">-->
<!--              <input v-model="active" type="checkbox" class="form-check-input" id="activeSwitch" />-->
<!--            </div>-->
<!--          </div>-->
<!--          <hr>-->
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h4 class="card-title mb-0">System</h4>
              <p class="text-muted mb-0 mt-1" style="font-size:13px;">Mark this notification type as a system type</p>
            </div>
            <div class="form-check form-switch form-switch-lg mb-0">
              <input v-model="system" type="checkbox" class="form-check-input" id="systemSwitch" />
            </div>
          </div>
        </div>
      </div>

      <input type="hidden" name="csrf_token" v-model="csrf_token">
    </form>
    <template v-slot:modal-footer>
      <b-button variant="light" @click="closeModal">Close</b-button>
      <b-button variant="primary" @click="addNotificationType" :disabled="tryingToSubmit">
        <b-spinner v-show="tryingToSubmit" small></b-spinner>
        Submit</b-button>
    </template>

  </b-modal>
</template>
